import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Eye, Clock, CheckCircle, XCircle, AlertTriangle, FileText } from 'lucide-react';
import api from '../../lib/api';

interface PendingAsset {
  id: string;
  name: string;
  category: string;
  tenantName: string;
  totalValue: number;
  submittedAt: string;
  documentsCount: number;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  daysInQueue: number;
}

const priorityColors: Record<string, string> = {
  LOW: 'bg-surface-600 text-surface-200',
  MEDIUM: 'bg-yellow-500/20 text-yellow-400',
  HIGH: 'bg-red-500/20 text-red-400',
};

const priorityLabels: Record<string, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
};

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundo',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios',
};

export function ComplianceQueue() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const { data: pendingAssets, isLoading } = useQuery({
    queryKey: ['compliance-queue', search, categoryFilter, priorityFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      const res = await api.get(`/tokenization/compliance/pending?${params}`);
      return res.data as PendingAsset[];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['compliance-stats'],
    queryFn: async () => {
      const res = await api.get('/tokenization/compliance/stats');
      return res.data;
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Fila de Compliance</h1>
        <p className="text-surface-400">Ativos aguardando análise e aprovação</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.pending || 0}</p>
              <p className="text-sm text-surface-400">Pendentes</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.approvedToday || 0}</p>
              <p className="text-sm text-surface-400">Aprovados Hoje</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.rejectedToday || 0}</p>
              <p className="text-sm text-surface-400">Rejeitados Hoje</p>
            </div>
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.highPriority || 0}</p>
              <p className="text-sm text-surface-400">Alta Prioridade</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar ativos..."
              className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
            />
          </div>
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          <option value="">Todas as Categorias</option>
          {Object.entries(categoryLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          <option value="">Todas as Prioridades</option>
          <option value="HIGH">Alta</option>
          <option value="MEDIUM">Média</option>
          <option value="LOW">Baixa</option>
        </select>
      </div>

      {/* Queue Table */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-surface-400">Carregando fila...</p>
          </div>
        ) : !pendingAssets?.length ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-surface-400">Nenhum ativo pendente de análise</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Ativo</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Emissor</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Categoria</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Valor</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Docs</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Prioridade</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Dias na Fila</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {pendingAssets.map((asset) => (
                <tr key={asset.id} className="hover:bg-surface-800/50 transition-colors">
                  <td className="px-4 py-4">
                    <p className="font-medium">{asset.name}</p>
                    <p className="text-sm text-surface-400">Enviado em {formatDate(asset.submittedAt)}</p>
                  </td>
                  <td className="px-4 py-4 text-sm">{asset.tenantName}</td>
                  <td className="px-4 py-4 text-sm">{categoryLabels[asset.category] || asset.category}</td>
                  <td className="px-4 py-4 text-right">{formatCurrency(asset.totalValue)}</td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <FileText className="w-4 h-4 text-surface-400" />
                      <span>{asset.documentsCount}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[asset.priority]}`}>
                      {priorityLabels[asset.priority]}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={asset.daysInQueue > 5 ? 'text-red-400' : asset.daysInQueue > 3 ? 'text-yellow-400' : ''}>
                      {asset.daysInQueue}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center">
                      <button
                        onClick={() => navigate(`/admin/compliance/review/${asset.id}`)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        Analisar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ComplianceQueue;
